import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography
} from '@mui/material';
import { Help, Close } from '@mui/icons-material';
import IconButton from '@mui/material/IconButton';

interface HelpModalProps {
  open: boolean;
  onClose: () => void;
}

const sectionTitle = { mb: 0.3, color: 'primary.main', fontWeight: 700, fontSize: '0.82rem' } as const;
const item = { mb: 0.2, fontSize: '0.75rem', lineHeight: 1.4 } as const;

const HelpModal: React.FC<HelpModalProps> = ({ open, onClose }) => {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth sx={{ zIndex: 10000 }}>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.8, px: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Help color="primary" sx={{ fontSize: 18 }} />
          <Typography variant="subtitle1" fontWeight={600} fontSize="0.88rem">Guia do SysEditor</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><Close sx={{ fontSize: 18 }} /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 1.5, py: 0.8 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>

          <Box>
            <Typography sx={sectionTitle}>📝 Sobre</Typography>
            <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.4 }}>
              Editor de texto rico com formatação avançada, tabelas inteligentes e integração com IA.
            </Typography>
          </Box>

          <Box>
            <Typography sx={sectionTitle}>✏️ Formatação</Typography>
            <Box sx={{ ml: 1.5 }}>
              <Typography variant="caption" sx={item}>• <strong>Fontes:</strong> 13 famílias disponíveis</Typography>
              <Typography variant="caption" sx={item}>• <strong>Tamanhos:</strong> 8pt a 72pt</Typography>
              <Typography variant="caption" sx={item}>• <strong>Estilos:</strong> Negrito, itálico, alinhamentos e parágrafos</Typography>
              <Typography variant="caption" sx={item}>• <strong>Listas:</strong> 5 tipos de numeração e marcadores</Typography>
            </Box>
          </Box>

          <Box>
            <Typography sx={sectionTitle}>📎 Inserção</Typography>
            <Box sx={{ ml: 1.5 }}>
              <Typography variant="caption" sx={item}>• <strong>Imagens:</strong> URL ou upload</Typography>
              <Typography variant="caption" sx={item}>• <strong>Tabelas:</strong> Criação personalizada</Typography>
              <Typography variant="caption" sx={item}>• <strong>Quebra de Página:</strong> Para impressão</Typography>
            </Box>
          </Box>

          <Box>
            <Typography sx={sectionTitle}>📊 Tabelas Avançadas</Typography>
            <Box sx={{ ml: 1.5 }}>
              <Typography variant="caption" sx={item}>• <strong>Redimensionar:</strong> Arraste bordas para ajustar</Typography>
              <Typography variant="caption" sx={item}>• <strong>Menu Contexto:</strong> Botão direito para inserir/excluir</Typography>
              <Typography variant="caption" sx={item}>• <strong>Mesclar:</strong> Células horizontal ou verticalmente</Typography>
              <Typography variant="caption" sx={item}>• <strong>Cores:</strong> Fundo e bordas personalizáveis</Typography>
            </Box>
          </Box>

          <Box>
            <Typography sx={sectionTitle}>🤖 IA</Typography>
            <Box sx={{ ml: 1.5 }}>
              <Typography variant="caption" sx={item}>• Clique no ícone ⭐ na toolbar</Typography>
              <Typography variant="caption" sx={item}>• Descreva o conteúdo desejado e edite antes de inserir</Typography>
            </Box>
          </Box>

          <Box>
            <Typography sx={sectionTitle}>⌨️ Atalhos</Typography>
            <Box sx={{ ml: 1.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
              <Typography variant="caption" sx={item}>• <strong>Ctrl+Z:</strong> Desfazer</Typography>
              <Typography variant="caption" sx={item}>• <strong>Ctrl+Y:</strong> Refazer</Typography>
              <Typography variant="caption" sx={item}>• <strong>Ctrl+B:</strong> Negrito</Typography>
              <Typography variant="caption" sx={item}>• <strong>Ctrl+I:</strong> Itálico</Typography>
              <Typography variant="caption" sx={item}>• <strong>Ctrl+E:</strong> Centralizar</Typography>
              <Typography variant="caption" sx={item}>• <strong>Ctrl+A:</strong> Selecionar tudo</Typography>
              <Typography variant="caption" sx={item}>• <strong>Ctrl+C/V:</strong> Copiar/Colar</Typography>
            </Box>
          </Box>

          <Box>
            <Typography sx={sectionTitle}>💡 Dicas</Typography>
            <Box sx={{ ml: 1.5 }}>
              <Typography variant="caption" sx={item}>• Duplo clique em imagem para redimensionar</Typography>
              <Typography variant="caption" sx={item}>• Handle circular no canto da tabela para redimensionar toda ela</Typography>
              <Typography variant="caption" sx={item}>• "Resetar Largura" no menu de contexto volta ao padrão</Typography>
            </Box>
          </Box>

        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 1.5, py: 0.6 }}>
        <Button onClick={onClose} variant="contained" size="small" sx={{ fontSize: '0.78rem' }}>
          Entendi
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default HelpModal;
